import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { eur } from "@/lib/format";
import { Search, Copy } from "lucide-react";
import { toast } from "sonner";

export const PRICING_MODES = [["none", "Nessuno"], ["percent", "Sconto percentuale"], ["custom", "Prezzi personalizzati per corso"]];
const PCT_FIELDS = [["discount_elearning_percent", "E-learning"], ["discount_videoconferenza_percent", "Videoconferenza"], ["discount_aula_percent", "Aula"]];

function PercentFields({ form, setForm }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="pricing-percent-fields">
      {PCT_FIELDS.map(([k, l]) => (
        <div key={k}>
          <Label className="text-xs">{l} (%)</Label>
          <Input type="number" step="0.5" min="0" max="100" value={form[k] ?? 0}
            onChange={(e) => setForm({ ...form, [k]: parseFloat(e.target.value || "0") })}
            data-testid={`partner-input-discount-${k.split("_")[1]}`} />
        </div>
      ))}
      <p className="sm:col-span-3 text-xs text-slate-500">Lo sconto si applica sul prezzo partner di listino del corso, in base alla sua modalità.</p>
    </div>
  );
}

function CopyFromPartner({ form, setForm, sources }) {
  const [src, setSrc] = useState("");
  const copy = () => {
    const from = sources.find((p) => p.id === src);
    if (!from) return;
    const current = form.custom_prices || {};
    const added = Object.entries(from.custom_prices || {}).filter(([cid]) => current[cid] === undefined || current[cid] === "" || current[cid] === null);
    setForm({ ...form, custom_prices: { ...current, ...Object.fromEntries(added) } });
    toast.success(added.length ? `${added.length} ${added.length === 1 ? "prezzo copiato" : "prezzi copiati"} da ${from.company_name}. Ricorda di salvare.` : "Nessun prezzo da copiare: i corsi hanno già un prezzo dedicato");
    setSrc("");
  };
  if (!sources.length) return null;
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-end rounded-xl border border-dashed border-slate-300 p-3" data-testid="pricing-copy-from">
      <div className="flex-1">
        <Label className="text-xs">Copia listino da un altro partner</Label>
        <Select value={src} onValueChange={setSrc}>
          <SelectTrigger data-testid="pricing-copy-source"><SelectValue placeholder="Scegli il partner di origine" /></SelectTrigger>
          <SelectContent>
            {sources.map((p) => <SelectItem key={p.id} value={p.id}>{p.company_name} · {Object.keys(p.custom_prices).length} prezzi</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button type="button" variant="outline" disabled={!src} onClick={copy} data-testid="pricing-copy-button"><Copy className="w-4 h-4 mr-1" /> Copia prezzi</Button>
      <p className="sm:hidden text-xs text-slate-500">Copia solo i corsi senza prezzo dedicato.</p>
    </div>
  );
}

function CustomPriceTable({ form, setForm, courses }) {
  const [q, setQ] = useState("");
  const prices = form.custom_prices || {};
  const shown = useMemo(() => courses.filter((c) => !q || c.title.toLowerCase().includes(q.toLowerCase())), [courses, q]);
  const setPrice = (id, v) => setForm({ ...form, custom_prices: { ...prices, [id]: v } });
  return (
    <div className="space-y-2" data-testid="pricing-custom-table">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Cerca corso…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="pricing-custom-search" />
      </div>
      <div className="border rounded-xl max-h-72 overflow-auto">
        <table className="w-full text-sm min-w-[520px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
            <tr><th className="p-2 text-left">Corso</th><th className="p-2 text-right">Prezzo standard</th><th className="p-2 text-right">Listino partner</th><th className="p-2 text-right w-36">Prezzo partner</th></tr>
          </thead>
          <tbody>
            {shown.map((c) => {
              const v = prices[c.id] ?? "";
              const over = v !== "" && Number(v) > Number(c.public_price || 0);
              return (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="p-2"><div className="font-medium">{c.title}</div><div className="text-xs text-slate-500 capitalize">{c.modality}{c.active === false ? " · disattivato" : ""}</div></td>
                  <td className="p-2 text-right font-mono">{eur(c.public_price || 0)}</td>
                  <td className="p-2 text-right font-mono text-slate-500">{eur(c.partner_price || 0)}</td>
                  <td className="p-2"><Input type="number" min="0" step="0.01" value={v} placeholder="Standard"
                    onChange={(e) => setPrice(c.id, e.target.value)} className={`h-8 text-right ${over ? "border-red-500" : ""}`}
                    data-testid={`pricing-custom-input-${c.id}`} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">Lascia vuoto per applicare il prezzo partner di listino. Il prezzo dedicato non può superare il prezzo standard.</p>
    </div>
  );
}

export function PartnerPricingPolicy({ form, setForm, courses, partners = [], editingId = null }) {
  const mode = form.pricing_mode || "none";
  const sources = partners.filter((p) => p.id !== editingId && Object.keys(p.custom_prices || {}).length > 0);
  return (
    <div className="col-span-2 border-t pt-3 mt-1 space-y-3" data-testid="partner-pricing-policy">
      <div className="text-sm font-semibold text-slate-800">Politica Commerciale</div>
      <div className="max-w-xs">
        <Label className="text-xs">Tipo di sconto</Label>
        <Select value={mode} onValueChange={(v) => setForm({ ...form, pricing_mode: v })}>
          <SelectTrigger data-testid="partner-pricing-mode"><SelectValue /></SelectTrigger>
          <SelectContent>{PRICING_MODES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {mode === "percent" && <PercentFields form={form} setForm={setForm} />}
      {mode === "custom" && <CopyFromPartner form={form} setForm={setForm} sources={sources} />}
      {mode === "custom" && <CustomPriceTable form={form} setForm={setForm} courses={courses} />}
      {mode === "none" && <p className="text-xs text-slate-500">Al partner si applica il prezzo partner di listino di ogni corso.</p>}
    </div>
  );
}
